import React from 'react';
import { BaseMaterialMonthData } from './BaseMaterialManagement';
import type { TimeGranularity } from '../../../utils/timeGranularity';
import { GRANULARITY_LABELS } from '../../../utils/timeGranularity';

interface Props {
  data: BaseMaterialMonthData[];
  matPrice: number;
  selectedMonthIndex?: number;
  derivedMaterials: Array<{ formula_code: string; formula_name: string; price_ratio: number }>;
  formulaColorDictionary: Record<string, string>;
  timeGranularity?: TimeGranularity;
}

export const BaseMaterialMonthDetail: React.FC<Props> = ({
  data,
  matPrice,
  selectedMonthIndex = 0,
  derivedMaterials,
  formulaColorDictionary,
  timeGranularity = 'Month',
}) => {
  const isAggregated = timeGranularity !== 'Month';
  // +1 to skip on-hand bar at index 0
  const dataIndex = selectedMonthIndex + 1;
  const d = data?.[dataIndex];

  if (!d) {
    return (
      <div className="min-w-[25rem] flex flex-col flex-1 bg-white shadow-md p-4">
        <div className="text-gray-400 text-sm text-center py-8">No data available</div>
      </div>
    );
  }

  const { totalSupply, totalDemand, buffer, fullDemand, hedges, totalCoverage, covPercentage, hedgedPercentage } = d;
  const totalPct = covPercentage + hedgedPercentage;
  const finalGap = fullDemand - totalCoverage;
  const isFullyCovered = finalGap <= 0;
  const maxVal = Math.max(totalCoverage, fullDemand) * 1.2 || 1;

  return (
    <div className="min-w-[25rem] flex flex-col flex-1 bg-white shadow-md p-4 max-h-full overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span style={{ color: 'rgba(59, 130, 246, 0.5)', fontSize: '0.65em' }}>&#9654;</span>
          <span className="text-base font-semibold text-gray-900">{d.month}</span>
          <span className="text-sm text-gray-400">{isAggregated ? `${GRANULARITY_LABELS[timeGranularity]} ` : ''}Base View</span>
        </div>
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${
          isFullyCovered ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {isFullyCovered ? '✓' : '⚠'} {totalPct}%
        </span>
      </div>
      {isAggregated && (
        <div className="text-xs text-gray-400 mb-3 leading-tight">
          Values are aggregated across all months in this {GRANULARITY_LABELS[timeGranularity].toLowerCase()} period.
        </div>
      )}

      {/* Comparison Bars */}
      <div className="space-y-3 mb-4">
        <div>
          <div className="text-xs font-medium text-gray-500 mb-1.5">Coverage (Supply + Hedges)</div>
          <div className="relative h-5 rounded-md bg-gray-100 overflow-hidden">
            {(() => {
              let left = 0;
              return derivedMaterials.map(dm => {
                const val = d.supplyByMaterial[dm.formula_code] || 0;
                const w = (val / maxVal) * 100;
                const l = left;
                left += w;
                return (
                  <div key={dm.formula_code} className="absolute h-full" style={{ left: `${l}%`, width: `${w}%`, backgroundColor: formulaColorDictionary[dm.formula_code] || '#6B7280' }} />
                );
              });
            })()}
            <div className="absolute h-full" style={{ left: `${(totalSupply / maxVal) * 100}%`, width: `${(hedges / maxVal) * 100}%`, backgroundColor: '#93C5FD' }} />
            <div className="absolute h-full w-0.5 z-10 bg-red-500" style={{ left: `${Math.min(100, (fullDemand / maxVal) * 100)}%` }} />
          </div>
        </div>

        <div>
          <div className="text-xs font-medium text-gray-500 mb-1.5">Demand + Buffer (base equiv.)</div>
          <div className="relative h-5 rounded-md bg-gray-100 overflow-hidden">
            {(() => {
              let left = 0;
              return derivedMaterials.flatMap(dm => {
                const demandVal = d.demandByMaterial[dm.formula_code] || 0;
                const bufferVal = d.bufferByMaterial?.[dm.formula_code] || 0;
                const color = formulaColorDictionary[dm.formula_code] || '#cfcfcf';
                const dw = (demandVal / maxVal) * 100;
                const bw = (bufferVal / maxVal) * 100;
                const dl = left;
                left += dw;
                const bl = left;
                left += bw;
                return [
                  <div key={`demand_${dm.formula_code}`} className="absolute h-full" style={{ left: `${dl}%`, width: `${dw}%`, backgroundColor: `${color}55` }} />,
                  bufferVal > 0 && (
                    <div key={`buffer_${dm.formula_code}`} className="absolute h-full" style={{
                      left: `${bl}%`, width: `${bw}%`,
                      background: `repeating-linear-gradient(45deg, ${color}30, ${color}30 3px, ${color}55 3px, ${color}55 6px)`,
                    }} />
                  ),
                ];
              });
            })()}
            <div className="absolute h-full w-0.5 z-10 bg-red-500" style={{ left: `${Math.min(100, (fullDemand / maxVal) * 100)}%` }} />
          </div>
        </div>
      </div>

      {/* Verdict */}
      <div className={`p-2.5 rounded-lg text-sm mb-4 ${isFullyCovered ? 'bg-green-50' : 'bg-red-50'}`}>
        {isFullyCovered ? (
          <span className="text-green-700 font-medium">✓ Fully covered for {d.month}</span>
        ) : (
          <div>
            <div className="text-red-700 font-medium">⚠ Unhedged exposure: {Math.round(finalGap).toLocaleString()} MT</div>
            <div className="text-red-600 text-xs mt-1">Physical {covPercentage}%{hedges > 0 ? ` + Hedged ${hedgedPercentage}%` : ''} = {totalPct}% total coverage</div>
          </div>
        )}
      </div>

      {/* Breakdown */}
      <div className="space-y-0 divide-y divide-gray-100">
        {derivedMaterials.map(dm => {
          const code = dm.formula_code;
          const color = formulaColorDictionary[code] || '#6B7280';
          const baseSupply = d.supplyByMaterial[code] || 0;
          const rawSupply = d.rawSupplyByMaterial[code] || 0;
          const ratio = d.ratioByMaterial[code] || 1;
          return (
            <div key={code} className="flex items-center justify-between py-1.5">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: color }} />
                <div className="flex flex-col">
                  <span className="text-sm text-gray-600">Supply: {dm.formula_name}</span>
                  {ratio !== 1 && <span className="text-[11px] text-gray-400">{Math.round(rawSupply).toLocaleString()} × {ratio.toFixed(2)}</span>}
                </div>
              </div>
              <span className="text-sm tabular-nums font-semibold text-gray-900 ml-2">{Math.round(baseSupply).toLocaleString()} MT</span>
            </div>
          );
        })}
        {hedges > 0 && (
          <div className="flex items-center justify-between py-1.5">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: '#93C5FD' }} />
              <span className="text-sm text-gray-600">Hedges (Base)</span>
            </div>
            <span className="text-sm tabular-nums font-semibold text-blue-600 ml-2">+{Math.round(hedges).toLocaleString()} MT</span>
          </div>
        )}
        {derivedMaterials.map(dm => {
          const code = dm.formula_code;
          const color = formulaColorDictionary[code] || '#cfcfcf';
          const baseDemand = d.demandByMaterial[code] || 0;
          const rawDemand = d.rawDemandByMaterial[code] || 0;
          const baseBuffer = d.bufferByMaterial?.[code] || 0;
          const rawBuffer = d.rawBufferByMaterial?.[code] || 0;
          const ratio = d.ratioByMaterial[code] || 1;
          return (
            <div key={`d_${code}`} className="flex items-center justify-between py-1.5">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: `${color}55` }} />
                <div className="flex flex-col">
                  <span className="text-sm text-gray-600">Demand: {dm.formula_name}</span>
                  {ratio !== 1 && <span className="text-[11px] text-gray-400">{Math.round(rawDemand).toLocaleString()}{rawBuffer > 0 ? ` + ${Math.round(rawBuffer).toLocaleString()} buf` : ''} × {ratio.toFixed(2)}</span>}
                </div>
              </div>
              <span className="text-sm tabular-nums font-semibold text-gray-900 ml-2">{Math.round(baseDemand + baseBuffer).toLocaleString()} MT</span>
            </div>
          );
        })}
        <div className="pt-2">
          <div className="flex items-center justify-between py-1.5">
            <span className="text-sm font-semibold text-gray-900">Total Need</span>
            <span className="text-sm tabular-nums font-bold text-gray-900">{Math.round(fullDemand).toLocaleString()} MT</span>
          </div>
          {buffer > 0 && <div className="ml-5 text-xs text-gray-400 pb-1">Demand {Math.round(totalDemand).toLocaleString()} + Buffer {Math.round(buffer).toLocaleString()}</div>}
        </div>
      </div>

    </div>
  );
};
